import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Link } from "react-router"
import { formatInt, localeForLanguage } from "../supplierDashboardStore"
import type { DashboardWidgetProps } from "../widgetRegistry"

const MC_BASE = "/material-compliance"

const ROWS: {
  labelKey: TranslationKeys
  value: (s: DashboardWidgetProps["stats"]) => number
  // Row links use the server-side `quality` filter (see F4-FE) so each
  // category deep-links to exactly its declarations, beyond the page cap.
  quality: "committed" | "extraction_empty" | "conflicting"
  warn?: boolean
}[] = [
  {
    labelKey: "MC_SSD_RQ_COMMITTED",
    value: (s) => s.replyQuality.committed,
    quality: "committed",
  },
  {
    labelKey: "MC_SSD_RQ_EMPTY",
    value: (s) => s.replyQuality.extractionEmpty,
    quality: "extraction_empty",
  },
  {
    labelKey: "MC_SSD_RQ_CONFLICTS",
    value: (s) => s.replyQuality.conflicts,
    quality: "conflicting",
    warn: true,
  },
]

export function ReplyQualityWidget({ stats }: DashboardWidgetProps) {
  const { translate, currentLanguage } = useTranslation()
  const locale = localeForLanguage(currentLanguage)
  return (
    <div>
      {ROWS.map((row) => {
        const warn = row.warn && row.value(stats) > 0
        return (
          <Link
            key={row.labelKey}
            to={`${MC_BASE}/supplier-studio/declarations?quality=${row.quality}`}
            className={cn(
              "-mx-1 flex items-baseline justify-between gap-4 rounded-sm border-t border-white/5 px-1 py-3 text-sm text-grey5 transition-colors first:border-t-0 hover:bg-white/3 hover:text-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
              warn && "text-warning hover:text-warning",
            )}
          >
            <span>{translate(row.labelKey)}</span>
            <b
              className={cn(
                "font-semibold tabular-nums text-white",
                warn && "text-warning",
              )}
            >
              {formatInt(row.value(stats), locale)}
            </b>
          </Link>
        )
      })}
    </div>
  )
}
